import * as Sentry from '@sentry/react';

const TOKEN_KEYS = ['authorization', 'access_token', 'refresh_token', 'id_token', 'token'];

// Default DSN fallback (can be overridden by VITE_SENTRY_DSN env var)
const DEFAULT_DSN = 'https://b2e8beea0423356fa88ebbcf829e7386@o4507527806386176.ingest.us.sentry.io/4510864022700032';

const isEnabled = (): boolean => {
  const dsn = (import.meta as any).env?.VITE_SENTRY_DSN || DEFAULT_DSN;
  return Boolean(dsn && (((import.meta as any).env?.PROD) || (import.meta as any).env?.VITE_VERCEL_ENV === 'preview' || (import.meta as any).env?.DEV));
};

const redactString = (value: string): string => {
  let result = value;
  for (const key of TOKEN_KEYS) {
    const re = new RegExp(`(${key}\\s*[=:]\\s*)([^\\s&]+)`, 'gi');
    result = result.replace(re, '$1[REDACTED]');
  }
  return result;
};

const sanitizeUrl = (rawUrl: string): string => {
  try {
    const url = new URL(rawUrl, window.location.origin);
    for (const [key] of url.searchParams.entries()) {
      if (TOKEN_KEYS.some((tokenKey) => key.toLowerCase().includes(tokenKey))) {
        url.searchParams.set(key, '[REDACTED]');
      }
    }
    return url.toString();
  } catch {
    return redactString(rawUrl);
  }
};

const sanitizeValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.includes('http') ? sanitizeUrl(value) : redactString(value);
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeValue);
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(record)) {
      if (TOKEN_KEYS.some((tokenKey) => key.toLowerCase().includes(tokenKey))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeValue(val);
      }
    }
    return sanitized;
  }

  return value;
};

export const initSentry = (): void => {
  if (!isEnabled()) return;

  // Use built-in browserTracingIntegration from @sentry/react v9+
  const tracingIntegration = (Sentry as any).browserTracingIntegration
    ? (Sentry as any).browserTracingIntegration()
    : undefined;

  const integrations = tracingIntegration ? [tracingIntegration] : [];

  Sentry.init({
    dsn: (import.meta as any).env?.VITE_SENTRY_DSN || DEFAULT_DSN,
    environment: (import.meta as any).env?.VITE_VERCEL_ENV || ((import.meta as any).env?.PROD ? 'production' : 'development'),
    release: (globalThis as any).__APP_RELEASE__ || (import.meta as any).env?.VITE_APP_RELEASE,
    integrations,
    tracesSampleRate: Number((import.meta as any).env?.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.1),
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    beforeSend(event: any) {
      return sanitizeValue(event) as any;
    },
  });

  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'Unhandled promise rejection')));
  });

  const addRouteBreadcrumb = (to: string) => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: 'route_change',
      data: { to: sanitizeUrl(to) },
      level: 'info',
    });
  };

  const originalPushState = history.pushState;
  history.pushState = function pushState(...args) {
    const url = args[2];
    if (typeof url === 'string') addRouteBreadcrumb(url);
    return originalPushState.apply(this, args as any);
  };

  const originalReplaceState = history.replaceState;
  history.replaceState = function replaceState(...args) {
    const url = args[2];
    if (typeof url === 'string') addRouteBreadcrumb(url);
    return originalReplaceState.apply(this, args as any);
  };

  window.addEventListener('popstate', () => {
    addRouteBreadcrumb(window.location.pathname + window.location.search);
  });
};

export const addSentryBreadcrumb = (message: string, data?: Record<string, unknown>) => {
  if (!isEnabled()) return;
  Sentry.addBreadcrumb({ category: 'pipeline', message, data: sanitizeValue(data) as Record<string, unknown>, level: 'info' });
};

export const setSentryAuthContext = (userId: string) => {
  if (!isEnabled()) return;
  Sentry.setUser({ id: userId });
  Sentry.setTag('auth_provider', 'google');
};

export const clearSentryAuthContext = () => {
  if (!isEnabled()) return;
  Sentry.setUser(null);
};

export const captureSentryException = (error: unknown, extras?: Record<string, unknown>) => {
  if (!isEnabled()) return;
  Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
    extra: sanitizeValue(extras) as Record<string, unknown>,
  });
};

export const captureSentryMessage = (message: string, extras?: Record<string, unknown>) => {
  if (!isEnabled()) return;
  Sentry.captureMessage(message, {
    level: 'warning',
    extra: sanitizeValue(extras) as Record<string, unknown>,
  });
};
